import React, { useState, useRef } from "react";
import { PDFDocument } from "pdf-lib";
import { saveAs } from "file-saver";

export default function PDFMergeSplit({ file, mode }) {
  const [mergeFile1, setMergeFile1] = useState(null);
  const [mergeFile2, setMergeFile2] = useState(null);
  const [splitPages, setSplitPages] = useState("");
  const [loading, setLoading] = useState(false);
  const [modeState, setMode] = useState(null);
  const cancelRef = useRef(false);

  // Only convert when user clicks Convert
  const handleMergeConvert = async () => {
    if (!mergeFile1 || !mergeFile2) return;
    setLoading(true);
    setMode('merge');
    cancelRef.current = false;
    const mergedPdf = await PDFDocument.create();
    const mergeFiles = [mergeFile1, mergeFile2];
    for (const file of mergeFiles) {
      if (cancelRef.current) {
        setLoading(false);
        setMode(null);
        return;
      }
      const bytes = await file.arrayBuffer();
      const pdf = await PDFDocument.load(bytes);
      const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
      copiedPages.forEach(page => mergedPdf.addPage(page));
    }
    if (!cancelRef.current) {
      const mergedBytes = await mergedPdf.save();
      saveAs(new Blob([mergedBytes]), "merged.pdf");
    }
    setLoading(false);
    setMode(null);
  };

  const handleSplitConvert = async () => {
    setLoading(true);
    setMode('split');
    cancelRef.current = false;
    if (!file) {
      setLoading(false);
      setMode(null);
      return;
    }
    const bytes = await file.arrayBuffer();
    const pdf = await PDFDocument.load(bytes);
    const totalPages = pdf.getPageCount();
    // Parse split points
    let points = splitPages.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n) && n > 0 && n < totalPages);
    points = Array.from(new Set(points)).sort((a, b) => a - b);
    if (points.length === 0) {
      alert("Enter valid split page numbers (e.g. 2,5,8)");
      setLoading(false);
      setMode(null);
      return;
    }
    let start = 0;
    let part = 1;
    for (let i = 0; i <= points.length; i++) {
      const end = (i < points.length) ? points[i] : totalPages;
      const newPdf = await PDFDocument.create();
      const pageIndices = [];
      for (let j = start; j < end; j++) pageIndices.push(j);
      if (pageIndices.length > 0) {
        const copiedPages = await newPdf.copyPages(pdf, pageIndices);
        copiedPages.forEach(p => newPdf.addPage(p));
        saveAs(new Blob([await newPdf.save()]), `part${part}.pdf`);
      }
      start = end;
      part++;
    }
    setLoading(false);
    setMode(null);
  };

  const handleCancel = () => {
    cancelRef.current = true;
    setLoading(false);
    setMode(null);
  };

  return (
    <div>
      {/* Merge UI */}
      {mode === "merge" && (
        <div style={{ marginBottom: 24 }}>
          <label style={{ fontWeight: 500, fontSize: 16, display: 'block', marginBottom: 12 }}>Select two PDF files to merge</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="file" accept="application/pdf" onChange={e => setMergeFile1(e.target.files[0] || null)} />
            <input type="file" accept="application/pdf" onChange={e => setMergeFile2(e.target.files[0] || null)} />
          </div>
        </div>
      )}
      {/* Split UI */}
      {mode === "split" && file && (
        <div style={{ marginBottom: 24 }}>
          <label style={{ fontWeight: 500, fontSize: 16, display: 'block', marginBottom: 12 }}>Enter split page numbers (e.g. 2,5,8)</label>
          <input type="text" value={splitPages} onChange={e => setSplitPages(e.target.value)} style={{ padding: 8, borderRadius: 6, border: '1px solid #ccc', width: '100%', fontSize: 15 }} />
        </div>
      )}
      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span>{modeState === "merge" ? "Merging..." : "Splitting..."}</span>
          <button onClick={handleCancel} style={{ color: 'red' }}>Cancel</button>
        </div>
      )}
      {/* Merge action */}
      {mode === "merge" && mergeFile1 && mergeFile2 && !loading && (
        <button onClick={handleMergeConvert} style={{ padding: '10px 24px', fontSize: 16, borderRadius: 8, background: '#007bff', color: '#fff', border: 'none', marginBottom: 16 }}>Merge</button>
      )}
      {/* Split action */}
      {mode === "split" && file && splitPages && !loading && (
        <button onClick={handleSplitConvert} style={{ padding: '10px 24px', fontSize: 16, borderRadius: 8, background: '#007bff', color: '#fff', border: 'none', marginBottom: 16 }}>Split</button>
      )}
    </div>
  );
}

